/**
 * hrLabels — HR 도메인 한글 라벨 맵 (Sprint 7B-9)
 *
 * 백엔드 코드/enum 값을 화면 표시용 한글로 일관되게 변환.
 * 모든 HR 페이지에서 import 해서 사용하면 영문/snake_case 가 노출되지 않음.
 *
 * 원칙
 *  - 사용자 화면 = 한글 우선. 영문 키는 disabled tooltip 으로만 노출
 *  - 모르는 코드는 원문 그대로 fallback (공백 회피)
 *  - 단일 소스 — 새 leave_code/template 추가 시 여기만 갱신
 */

// ─── 휴가 unit ───────────────────────────────
export const LEAVE_UNIT_LABEL: Record<string, string> = {
  full: '종일',
  half_am: '오전 반차',
  half_pm: '오후 반차',
  // 호환: 과거 quarter (Sprint 7B-6에서 제거) 들어와도 표시 가능
  quarter: '반반차',
};

// ─── leave_code → 한글 휴가명 ─────────────────
// (DB leave_types 의 name 과 일치시켜 fallback 시도)
export const LEAVE_CODE_LABEL: Record<string, string> = {
  annual: '연차',
  monthly: '월차',
  // 2026-08: 실데이터(DEV leave 431건)에 존재하나 라벨이 없어 영문 그대로 노출되던 코드
  personal: '개인 휴가',
  sick: '병가',
  family: '경조',
  maternity: '출산휴가',
  paternity: '배우자 출산휴가',
  childcare: '육아휴직',
  marriage: '결혼휴가',
  bereavement: '경조휴가',
  public: '공가',
  unpaid: '무급휴가',
  custom: '맞춤휴가',
};

// ─── 결재자 규칙 ─────────────────────────────
export const APPROVER_RULE_LABEL: Record<string, string> = {
  team_leader: '팀장',
  parent_team_leader: '상위 팀장',
  'role:superadmin': '대표',
  'role:admin': '관리자',
  'role:hr': '인사팀',
};

// ─── 결재 상태 ───────────────────────────────
export const APPROVAL_STATUS_LABEL: Record<string, string> = {
  pending: '진행 중',
  approved: '승인',
  rejected: '반려',
  cancelled: '취소',
  skipped: '스킵',
  delegated: '대결',
  draft: '임시 저장',
};

// ─── 휴가 상태 ───────────────────────────────
export const LEAVE_STATUS_LABEL: Record<string, string> = {
  pending: '승인 대기',
  approved: '승인',
  rejected: '반려',
  cancelled: '취소',
};

// ─── payload 필드 라벨 (결재 상세 모달용) ─────
export const APPROVAL_PAYLOAD_LABEL: Record<string, string> = {
  // 공통
  reason: '사유',
  document_type: '증명서 종류',
  // 일자
  work_date: '근무일',
  claim_date: '지출일',
  start_date: '시작일',
  end_date: '종료일',
  // 수치
  hours: '시간',
  amount: '금액',
  days_count: '일수',
  // 휴가 도메인
  leave_code: '휴가 종류',
  unit: '단위',
  // 인사이동
  from_team: '현재 팀',
  to_team: '이동 팀',
  effective_date: '발효일',
  // 채용
  candidate_name: '후보자',
  position: '포지션',
  // Sprint 7B-12A — 증명서 발급
  purpose: '제출처',
  issued_at: '발급일',
  include_salary: '연봉 표기',
  salary: '연봉',
  main_duties: '주요 담당 업무',
  tax_year: '귀속 연도',
  // Sprint 47: document_template_code 라벨 제거 — 같은 모달에 양식 이름이 별도 행으로 표시되어 중복
  document_template_name: '양식',
  subject_user_id: '대상자',
  target_user_id: '대상자',
  approver_user_id: '결재자',
  mentor_user_id: '멘토',
  mentee_user_id: '멘티',
  delegator_user_id: '위임자',
  delegatee_user_id: '대결자',
  effective_from: '발효일',
  effective_to: '종료일',
  handover_user_id: '인수자',
  successor_user_id: '후임자',
  // 2026-08: 근태 조정 신청(attendance_adjust) — 라벨이 없어 결재 상세에 snake_case 가 그대로 노출됐다
  adjustment_type: '조정 유형',
  target_work_date: '대상 근무일',
  new_check_in_at: '변경 출근 시각',
  new_check_out_at: '변경 퇴근 시각',
  new_work_location: '근무 장소',
  // 구버전 재택근무 신청 폼이 work_date 대신 date 로 저장한 건이 실제로 있다 (PROD/DEV 6건)
  date: '근무일',
  // 교육 수료증(document_issue) 계열
  course_title: '교육 과정',
  course_category: '교육 분류',
  total_hours: '총 교육 시간',
  completed_at: '수료 일시',
};

// ─── 근태 조정 유형 ──────────────────────────
export const ADJUSTMENT_TYPE_LABEL: Record<string, string> = {
  add_record: '기록 추가',
  edit_check_in: '출근 시각 수정',
  edit_check_out: '퇴근 시각 수정',
  cancel_record: '기록 취소',
};

// ─── 근무 장소 ───────────────────────────────
export const WORK_LOCATION_LABEL: Record<string, string> = {
  office: '사무실',
  remote: '재택',
  outside: '외부',
};

// ─── 교육 분류 ───────────────────────────────
export const COURSE_CATEGORY_LABEL: Record<string, string> = {
  culture: '조직문화',
  onboarding: '입문 교육',
  product: '제품 교육',
  skill: '직무 역량',
  safety: '안전 교육',
  compliance: '컴플라이언스',
};

/**
 * Sprint 47: payload 안에서 사용자 UUID 를 담은 키 집합.
 * formatPayloadValue 가 userNameMap 를 받으면 자동 변환.
 */
export const USER_ID_KEYS = new Set<string>([
  'subject_user_id', 'target_user_id', 'approver_user_id',
  'mentor_user_id', 'mentee_user_id',
  'delegator_user_id', 'delegatee_user_id',
  'handover_user_id', 'successor_user_id',
  'requester_user_id', 'reviewer_user_id', 'manager_user_id',
]);

/** Sprint 47: 모달에서 표시 자체를 숨길 키 (다른 행에 같은 정보가 있는 등). */
export const HIDDEN_PAYLOAD_KEYS = new Set<string>([
  'document_template_code', // 양식 이름이 별도 행으로 표시됨
  // 2026-08: 내부 식별자 — 사람에게 의미 없는 UUID 라 노출하지 않는다
  'target_record_id',
]);

// ─── Sprint 7B-12A: 문서 양식 카테고리 ──────────
export const DOCUMENT_CATEGORY_LABEL: Record<string, string> = {
  certificate: '증명서',
  contract: '계약서',
  company_doc: '회사 문서',
  internal_memo: '내부 메모',
};

// ─── Sprint 7B-12A: 문서 렌더 방식 ──────────────
export const DOCUMENT_RENDER_MODE_LABEL: Record<string, string> = {
  handlebars: '자동 PDF 발급',
  upload: '파일 업로드 + 변수 치환',
  external: '외부 발급 (수동)',
};

// ─── Sprint 7B-12A: 증명서 발급 상태 ────────────
export const ISSUANCE_STATUS_LABEL: Record<string, string> = {
  pending: '결재 대기',
  approved: '승인됨',
  generated: '생성 중',
  issued: '발급 완료',
  voided: '무효화',
};

// ─── 결재 카테고리 ───────────────────────────
export const APPROVAL_CATEGORY_LABEL: Record<string, string> = {
  hr_leave: '휴가',
  hr_attendance: '근태',
  hr_general: '일반',
  expense: '경비',
};

// ─── 통합 키 ─────────────────────────────────
export const INTEGRATION_LABEL: Record<string, string> = {
  slack: 'Slack 연동',
  google_calendar: '구글 캘린더 연동',
  email_fallback: '이메일 알림',
};

// ─── 직위/팀 역할 ────────────────────────────
export const TEAM_ROLE_LABEL: Record<string, string> = {
  leader: '팀장',
  deputy: '부팀장',
  member: '팀원',
  observer: '참관',
};

export const ROLE_LABEL: Record<string, string> = {
  superadmin: '대표',
  admin: '관리자',
  user: '직원',
};

// ─── 재직 상태 ───────────────────────────────
export const EMPLOYMENT_STATUS_LABEL: Record<string, string> = {
  active: '재직',
  leave: '휴직',
  resigned: '퇴직',
};

/**
 * 다음 연차 부여의 종류 (2026-08 S6 · 서버 `leaveNextGrantService.kind`).
 *   monthly           입사 1년 미만 — 매월 응당일에 1일
 *   first_anniversary 만 11개월을 채워 다음 부여가 곧 1주년 정식 연차
 *   anniversary       만 1년 이상 — 입사 기념일마다 새 연차
 */
export const NEXT_GRANT_KIND_LABEL: Record<string, string> = {
  monthly: '월차',
  first_anniversary: '첫 연차',
  anniversary: '연차',
};

// ─── 급여 수당 항목 (payroll_records.allowances 키) ───
// 키는 관리자가 자유롭게 넣는다 — 모르는 키는 입력값 그대로 보인다(한글로 넣었으면 한글).
export const ALLOWANCE_LABEL: Record<string, string> = {
  meal: '식대',
  transport: '교통비',
  overtime: '연장근무 수당',
  night: '야간근무 수당',
  holiday: '휴일근무 수당',
};

// ─── 헬퍼 함수들 ─────────────────────────────

/** 라벨 lookup with fallback */
function lookup(map: Record<string, string>, code: string | null | undefined): string {
  if (!code) return '—';
  return map[code] || code;
}

export const leaveUnitLabel = (u?: string | null) => lookup(LEAVE_UNIT_LABEL, u);
export const leaveCodeLabel = (c?: string | null) => lookup(LEAVE_CODE_LABEL, c);
export const approverRuleLabel = (r?: string | null) => lookup(APPROVER_RULE_LABEL, r);
export const approvalStatusLabel = (s?: string | null) => lookup(APPROVAL_STATUS_LABEL, s);
export const leaveStatusLabel = (s?: string | null) => lookup(LEAVE_STATUS_LABEL, s);
export const approvalPayloadLabel = (k: string) => lookup(APPROVAL_PAYLOAD_LABEL, k);
export const approvalCategoryLabel = (c?: string | null) => lookup(APPROVAL_CATEGORY_LABEL, c);
export const integrationLabel = (k?: string | null) => lookup(INTEGRATION_LABEL, k);
export const teamRoleLabel = (r?: string | null) => lookup(TEAM_ROLE_LABEL, r);
export const roleLabel = (r?: string | null) => lookup(ROLE_LABEL, r);
export const employmentStatusLabel = (s?: string | null) => lookup(EMPLOYMENT_STATUS_LABEL, s);
export const nextGrantKindLabel = (k?: string | null) => lookup(NEXT_GRANT_KIND_LABEL, k);
export const adjustmentTypeLabel = (t?: string | null) => lookup(ADJUSTMENT_TYPE_LABEL, t);
export const workLocationLabel = (l?: string | null) => lookup(WORK_LOCATION_LABEL, l);
export const courseCategoryLabel = (c?: string | null) => lookup(COURSE_CATEGORY_LABEL, c);
export const allowanceLabel = (k?: string | null) => lookup(ALLOWANCE_LABEL, k);

/**
 * ISO 문자열 → `YYYY-MM-DD HH:MM` (한국시간 고정).
 *
 * 2026-08: 결재 상세에 `2026-07-28T09:26:00+09:00` 형태가 그대로 노출돼 읽기 어려웠다.
 * 브라우저 로컬 시간대에 의존하는 `new Date().getHours()` 방식과 달리 **Asia/Seoul 로 고정**하므로
 * 해외에서 접속해도 한국 근무시각이 그대로 보인다.
 */
const KST_PARTS = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Seoul',
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hour12: false,
});

export function formatKstDateTime(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso); // 파싱 불가하면 원문 유지 (공백 회피)
  const p: Record<string, string> = {};
  for (const part of KST_PARTS.formatToParts(d)) p[part.type] = part.value;
  // en-CA + formatToParts 는 hour 를 24시간제로 주지만, 자정을 '24' 로 주는 환경이 있어 보정
  const hour = p.hour === '24' ? '00' : p.hour;
  return `${p.year}-${p.month}-${p.day} ${hour}:${p.minute}`;
}

/** ISO 8601 날짜+시각처럼 보이는 문자열인가 (날짜만인 `2026-07-28` 은 제외) */
function looksLikeIsoDateTime(v: unknown): v is string {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(v);
}

/**
 * payload 값을 사람이 읽기 쉬운 한글 형태로.
 * (금액, 시간, 휴가 단위, 휴가 코드 등 자동 포맷)
 *
 * Sprint 47: opts.userNameMap / opts.templateMap 추가
 *  - USER_ID_KEYS 의 UUID → 사용자 이름 (없으면 짧은 prefix 노출)
 *  - document_template_code → 양식 한글 이름
 */
export interface FormatPayloadOptions {
  userNameMap?: Record<string, string>;
  templateMap?: Record<string, string>;
}

export function formatPayloadValue(key: string, value: any, opts: FormatPayloadOptions = {}): string {
  if (value == null || value === '') return '—';

  // Sprint 47: 사용자 ID 키 → 이름 변환
  if (USER_ID_KEYS.has(key) && typeof value === 'string') {
    const name = opts.userNameMap?.[value];
    if (name) return name;
    return value.slice(0, 8) + '…';
  }

  // Sprint 47: 양식 코드 → 양식 이름
  if (key === 'document_template_code' && typeof value === 'string') {
    return opts.templateMap?.[value] || value;
  }

  // 2026-08: boolean 이 `true`/`false` 영문 그대로 노출됐다 (예: 증명서 «연봉 표기»)
  if (typeof value === 'boolean') return value ? '예' : '아니오';

  switch (key) {
    case 'amount':
      return `${Number(value).toLocaleString('ko-KR')}원`;
    case 'hours':
    case 'total_hours':
      return `${value}시간`;
    case 'days_count':
      return `${Number(value)}일`;
    case 'unit':
      return leaveUnitLabel(String(value));
    case 'leave_code':
      return leaveCodeLabel(String(value));
    // 2026-08: 근태 조정 신청의 영문 enum
    case 'adjustment_type':
      return adjustmentTypeLabel(String(value));
    case 'new_work_location':
    case 'work_location':
      return workLocationLabel(String(value));
    case 'course_category':
      return courseCategoryLabel(String(value));
    case 'work_date':
    case 'claim_date':
    case 'start_date':
    case 'end_date':
    case 'effective_date':
    case 'effective_from':
    case 'effective_to':
    case 'issued_at':
    case 'target_work_date':
    case 'date':
      return typeof value === 'string' ? value : String(value);
    default:
      if (typeof value === 'object') return JSON.stringify(value);
      // 2026-08: `_at` 로 끝나는 시각 필드는 한국시간 `YYYY-MM-DD HH:MM` 로 통일.
      //   키를 하나씩 열거하지 않고 형태로 판정하므로 새 시각 필드가 추가돼도 자동 적용된다.
      if (key.endsWith('_at') && looksLikeIsoDateTime(value)) return formatKstDateTime(value);
      return String(value);
  }
}

/* ── 슬랙 계정 연결 (F-3) ────────────────────────────────────────────────
 * 화면에 영문 코드가 그대로 나가지 않게 한다 (Korean-first 절대 규칙).
 * 'Slack' 은 외부 서비스명이라 영문 허용 대상이다. */

export function linkMethodLabel(code?: string | null): string {
  switch (code) {
    case 'email_auto': return '이메일 자동';
    case 'admin_manual': return '관리자 지정';
    case 'self_oauth': return '본인 연결';
    case 'self_code': return '본인 확인';
    default: return '-';
  }
}

export function linkStatusLabel(code?: string | null): string {
  switch (code) {
    case 'verified': return '연결됨';
    case 'unverified': return '확인 대기';
    case 'unlinked': return '미연결';
    default: return '-';
  }
}

/** 발송이 안 된 사유 — 관리자가 「왜 안 왔나」를 이 표현으로 읽는다. */
export function dmSkipReasonLabel(code?: string | null): string {
  switch (code) {
    case 'disabled_env': return '서버에서 슬랙 발송이 꺼져 있음';
    case 'disabled_admin': return '관리자 설정에서 슬랙이 꺼져 있음';
    case 'no_token': return '슬랙 접속 토큰 없음';
    case 'no_link': return '슬랙 계정이 연결되지 않음';
    case 'not_verified': return '연결이 확인 대기 상태';
    case 'pref_off': return '본인이 이 분류의 슬랙 알림을 끔';
    case 'inactive_user': return '퇴사·비활성 계정이라 보내지 않음';
    case 'redirect_mismatch': return '개발 환경 — 확인 숫자는 다른 사람에게 보내지 않음';
    case 'duplicate': return '이미 보낸 건';
    case 'claim_error': return '중복 확인 실패';
    case 'timeout': return '집계 시간 초과';
    case 'ratelimited': return '슬랙 속도 제한';
    case 'channel_not_found': return '슬랙 대화방을 찾을 수 없음';
    case 'missing_scope': return '슬랙 앱 권한 부족';
    case 'invalid_auth': return '슬랙 토큰이 유효하지 않음';
    case 'exception': return '처리 중 오류';
    default: return code || '-';
  }
}

/** 발송 상태 */
export function dmStatusLabel(code?: string | null): string {
  switch (code) {
    case 'sent': return '발송됨';
    case 'failed': return '발송 실패';
    case 'skipped': return '보내지 않음';
    case 'claimed': return '발송 중';
    default: return code || '-';
  }
}

// ─────────────────────────────────────────────────────────
// 내 할 일 · 업무 요청 (2026-09-11)
// ⛔ DB 에 enum 을 하나 늘릴 때마다 여기도 같이 늘린다. 화면에 영문 코드가 나가면 안 된다.
// ─────────────────────────────────────────────────────────

/** 할 일 상태 */
export function todoStatusLabel(code?: string | null): string {
  switch (code) {
    case 'todo': return '시작 전';
    case 'doing': return '진행 중';
    case 'done': return '완료';
    default: return code || '-';
  }
}

/**
 * 할 일 분류(태그).
 * ⛔ 값 목록의 정본은 서버 `src/services/hr/todoService.js` 의 CATEGORIES 다.
 *    거기에 값을 늘리면 여기 라벨도 함께 늘린다 — 화면에 영문 코드가 보이면 안 된다.
 */
export function todoCategoryLabel(code?: string | null): string {
  switch (code) {
    case 'accounting': return '회계';
    case 'management': return '관리';
    case 'etc': return '기타';
    default: return code || '-';
  }
}

/** 할 일이 어디서 왔나 */
export function todoSourceLabel(code?: string | null): string {
  switch (code) {
    case 'self': return '직접 추가';
    case 'request': return '업무 요청';
    case 'meeting': return '회의';
    case 'consult': return '상담';
    case 'recurrence': return '반복';
    case 'bot': return '카톡봇';
    default: return code || '-';
  }
}

/** 업무 요청 상태 */
export function taskRequestStatusLabel(code?: string | null): string {
  switch (code) {
    case 'pending': return '대기 중';
    case 'accepted': return '수락됨';
    case 'rejected': return '거절됨';
    case 'cancelled': return '취소됨';
    default: return code || '-';
  }
}

/**
 * 내 할 일 「구분」 (탭 대신 선택 하나 — 2026-09-27 개편).
 * ⛔ 값 목록의 정본은 서버 `todoService.SECTIONS` 다. 늘리면 여기 라벨도 함께.
 */
export function todoSectionLabel(code?: string | null): string {
  switch (code) {
    case 'all': return '전체';
    case 'received': return '받은 요청';
    case 'sent': return '보낸 요청';
    case 'recurring': return '반복 업무';
    case 'self': return '직접 등록';
    case 'consult': return '상담';
    case 'meeting': return '회의';
    default: return code || '-';
  }
}

/**
 * 보낸 할 일 카드에 붙는 한 줄 — 받은 사람과 요청의 형편.
 *   대기 → «→ 김혜진 · 수락 대기» / 수락(연결 중) → «김혜진 담당 · 진행 중»
 *   거절 → «김혜진 거절» / 받은 사람이 지움 → «김혜진이 목록에서 지움» / 취소 → 표시 없음('')
 * 상대 상태 라벨은 todoStatusLabel 을 쓴다(영문 코드가 보이면 안 된다).
 */
export function todoOutgoingLabel(o?: {
  status?: string | null; assignee_name?: string | null; live?: boolean;
  peer_status?: string | null; peer_deleted?: boolean;
} | null): string {
  if (!o) return '';
  const who = (o.assignee_name || '').trim() || '받는 사람';
  if (o.status === 'pending') return `→ ${who} · 수락 대기`;
  if (o.status === 'accepted') {
    if (o.peer_deleted) return `${who}이(가) 목록에서 지움`;
    return o.peer_status ? `${who} 담당 · ${todoStatusLabel(o.peer_status)}` : `${who} 담당 · 수락함`;
  }
  if (o.status === 'rejected') return `${who} 거절`;
  return '';
}

/** 회의 실행항목이 발행 때 갈 곳 */
export function actionItemTargetLabel(code?: string | null): string {
  switch (code) {
    case 'project': return '진행 프로젝트';
    case 'todo': return '담당자의 할 일';
    default: return '진행 프로젝트';   // 비었으면 기본값과 같은 곳이다
  }
}

// ─── 회사 관리 화면의 진행 상태 (리뷰·목표·복지·입퇴사 할 일·계약·자동 작업) ─────
// 2026-10-15: 회사 관리 화면에 pending_self 같은 영문 코드가 그대로 보였다. 모르는 코드는 «기타»(영문 노출 금지)
const ADMIN_STATUS_LABEL: Record<string, string> = {
  pending_self: '자기평가 대기',
  pending_peer: '동료 피드백 대기',
  pending_manager: '매니저 평가 대기',
  pending: '대기',
  draft: '작성 중',
  active: '진행 중',
  in_progress: '진행 중',
  open: '진행 중',
  completed: '완료',
  done: '완료',
  achieved: '달성',
  // 목표(draft|active|at_risk|achieved|missed|cancelled) · 교육(enrolled|in_progress|completed|dropped) — Codex P-1
  at_risk: '주의 필요',
  missed: '미달성',
  enrolled: '수강 신청',
  dropped: '중도 포기',
  approved: '승인',
  rejected: '반려',
  cancelled: '취소',
  canceled: '취소',
  skipped: '건너뜀',
  expired: '만료',
  terminated: '해지',
  pending_signature: '서명 대기',
  signed: '서명 완료',
  voided: '무효',
  success: '성공',
  succeeded: '성공',
  failed: '실패',
  error: '오류',
  running: '실행 중',
  // 급여 정산 기간(open|calculating|finalized|locked) · 복지 신청(submitted|approved|rejected|reimbursed) — Codex M-2
  calculating: '계산 중',
  finalized: '확정',
  locked: '잠김',
  submitted: '제출',
  reimbursed: '지급 완료',
  // 그 밖에 회사 관리 화면 표에 나오는 상태들
  ok: '정상',
  sent: '발송 완료',
  sending: '발송 중',
  scheduled: '예약',
  published: '게시',
  archived: '보관',
  todo: '할 일',
  doing: '진행 중',
  processing: '처리 중',
  resolved: '처리 완료',
  accepted: '수락',
  claimed: '맡음',
  stale: '오래됨',
  needs_check: '확인 필요',
  needs_recovery: '복구 필요',
};
/** 모르는 상태는 영문 코드 대신 «기타» — 사용자 화면에 영문 코드를 보이지 않는다(Korean-first) */
export function adminStatusLabel(code?: string | null): string {
  if (!code) return '—';
  return ADMIN_STATUS_LABEL[code] || '기타';
}
